import type { PointerEvent as ReactPointerEvent, RefObject } from "react";
import type { Corner } from "@softmax/web-features/site";
import type { Clip } from "@softmax/avatar/character";
import { reducedMotion } from "./widget-motion";
const dragStart = 6;
const tiltGain = 9;
const tiltMax = 14;
const tiltRest = 120;
type SetClip = React.Dispatch<React.SetStateAction<Clip | null>>;
type Facing = "left" | "right";
type DragContext = Readonly<{
  root: HTMLDivElement;
  dock: HTMLDivElement;
  glideAnimation: RefObject<Animation | null>;
  dragged: RefObject<boolean>;
  dragging: RefObject<boolean>;
  endDrag: RefObject<(() => void) | null>;
  setClip: SetClip;
  setFacing: (facing: Facing | null) => void;
  glide: (from: DOMRect) => void;
  moveTo: (destination: Readonly<{ next: Corner; from: DOMRect }>) => void;
}>;
function stopGlide(context: DragContext): {
  readonly x: number;
  readonly y: number;
} {
  const animation = context.glideAnimation.current;

  if (!animation) return { x: 0, y: 0 };

  const from = context.dock.getBoundingClientRect();
  animation.cancel();
  context.glideAnimation.current = null;
  const to = context.dock.getBoundingClientRect();
  const offset = { x: from.left - to.left, y: from.top - to.top };
  context.root.style.translate = `${offset.x}px ${offset.y}px`;
  context.setClip(null);
  return offset;
}
export function startDrag({
  event,
  context,
}: Readonly<{
  event: ReactPointerEvent;
  context: DragContext;
}>): void {
  if (event.button !== 0) return;

  context.endDrag.current?.();
  const el = context.root;
  const id = event.pointerId;
  const start = { x: event.clientX, y: event.clientY };
  const base = stopGlide(context);
  let moving = false;
  let lastX = event.clientX;
  let lastAt = event.timeStamp;
  let tilt = 0;
  let restTimer = 0;
  const lean = (degrees: number) =>
    el.style.setProperty("--carry-tilt", `${degrees.toFixed(1)}deg`);
  function move(pointer: PointerEvent) {
    if (pointer.pointerId !== id) return;

    const x = pointer.clientX - start.x;
    const y = pointer.clientY - start.y;

    if (!moving && Math.hypot(x, y) < dragStart) return;

    if (!moving) {
      moving = true;
      context.dragging.current = true;
      el.dataset.dragging = "";

      if (!reducedMotion()) context.setClip("fall");
    }

    el.style.translate = `${base.x + x}px ${base.y + y}px`;
    context.setFacing(
      pointer.clientX < window.innerWidth / 2 ? "right" : "left",
    );

    if (reducedMotion()) return;

    const speed =
      (pointer.clientX - lastX) / Math.max(1, pointer.timeStamp - lastAt);
    lastX = pointer.clientX;
    lastAt = pointer.timeStamp;
    const target = Math.max(-tiltMax, Math.min(tiltMax, speed * tiltGain));
    tilt += (target - tilt) * 0.35;
    lean(tilt);
    window.clearTimeout(restTimer);
    restTimer = window.setTimeout(() => {
      tilt = 0;
      lean(tilt);
    }, tiltRest);
  }
  function stop() {
    window.clearTimeout(restTimer);
    el.style.removeProperty("--carry-tilt");
    context.endDrag.current = null;
    context.dragging.current = false;
    context.setFacing(null);
    delete el.dataset.dragging;
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    window.removeEventListener("pointercancel", cancel);
  }
  function up(pointer: PointerEvent) {
    if (pointer.pointerId !== id) return;

    stop();

    if (!moving) return;

    context.dragged.current = true;
    // A drag ending outside the character has no click to consume.
    const clearClick = () => {
      window.clearTimeout(clickTimer);
      context.dragged.current = false;
      context.endDrag.current = null;
    };
    const clickTimer = window.setTimeout(clearClick);
    context.endDrag.current = clearClick;
    const top = pointer.clientY < window.innerHeight / 2;
    const left = pointer.clientX < window.innerWidth / 2;
    context.moveTo({
      next: `${top ? "top" : "bottom"}-${left ? "left" : "right"}`,
      from: context.dock.getBoundingClientRect(),
    });
  }
  function cancel(pointer: PointerEvent) {
    if (pointer.pointerId !== id) return;

    stop();

    if (moving) context.glide(context.dock.getBoundingClientRect());
  }
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
  window.addEventListener("pointercancel", cancel);
  context.endDrag.current = stop;
}
